import React, { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Bell } from "lucide-react";
import { useSupabaseUser } from "@/hooks/use-supabase-user";
import { useUserRoles } from "@/hooks/useUserRoles";

interface NotificationPreferencesProps {
  userId: string;
}

interface Preferences {
  notify_leave_requests: boolean;
  notify_swap_requests: boolean;
  notify_behavior_records: boolean;
}

export const NotificationPreferences = ({ userId }: NotificationPreferencesProps) => {
  const { toast } = useToast();
  const { user } = useSupabaseUser();
  const { isAdmin } = useUserRoles();
  const [preferences, setPreferences] = useState<Preferences | null>(null);
  const [loading, setLoading] = useState(true);
  const isOwnProfile = user?.id === userId;
  const canEdit = isOwnProfile || isAdmin;

  useEffect(() => {
    fetchPreferences();
  }, [userId]);

  const fetchPreferences = async () => {
    try {
      const { data, error } = await supabase
        .from("notification_preferences")
        .select("*")
        .eq("user_id", userId)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setPreferences(data);
      } else {
        // Create default preferences if none exist
        const { data: newPrefs, error: insertError } = await supabase
          .from("notification_preferences")
          .insert({ user_id: userId })
          .select()
          .single();

        if (insertError) throw insertError;
        setPreferences(newPrefs);
      }
    } catch (error: any) {
      console.error("Error fetching notification preferences:", error);
      toast({
        title: "Error",
        description: "Failed to load notification preferences",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const updatePreference = async (key: keyof Preferences, value: boolean) => {
    if (!canEdit) {
      toast({
        title: "Permission denied",
        description: "You don't have permission to modify these preferences",
        variant: "destructive",
      });
      return;
    }

    try {
      const { error } = await supabase
        .from("notification_preferences")
        .update({ [key]: value })
        .eq("user_id", userId);

      if (error) throw error;

      setPreferences((prev) => prev ? { ...prev, [key]: value } : null);

      toast({
        title: "Success",
        description: "Notification preferences updated",
      });
    } catch (error: any) {
      console.error("Error updating notification preferences:", error);
      toast({
        title: "Error",
        description: "Failed to update notification preferences",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return <div className="text-sm text-muted-foreground">Loading preferences...</div>;
  }

  if (!preferences) {
    return <div className="text-sm text-muted-foreground">No preferences found</div>;
  }

  const notificationTypes = [
    { key: "notify_leave_requests" as keyof Preferences, label: "Leave Requests" },
    { key: "notify_swap_requests" as keyof Preferences, label: "Swap Requests" },
    { key: "notify_behavior_records" as keyof Preferences, label: "Behavior Records / Performance Reviews" },
  ];

  return (
    <Card className="p-4">
      <div className="flex items-center gap-2 mb-4">
        <Bell className="h-4 w-4" />
        <h3 className="font-semibold">Notification Preferences</h3>
      </div>
      <div className="space-y-4">
        {notificationTypes.map(({ key, label }) => (
          <div key={key} className="flex items-center justify-between">
            <Label htmlFor={key} className="text-sm">{label}</Label>
            <Switch
              id={key}
              checked={preferences[key]}
              onCheckedChange={(checked) => updatePreference(key, checked)}
              disabled={!canEdit}
            />
          </div>
        ))}
      </div>
    </Card>
  );
};
